"use client";
import { Box, Stack, Heading, Text } from "@chakra-ui/react";
import Link from "next/link";
import Image from "next/image";
import { FiArrowUpRight } from "@onemind-services-llc/react-icons-ng/fi";
import PageLayout from "@/layouts/pageLayout";
import { Button } from "@/elements";
import { scrollToElement } from "@/utils/scrollToElement";
import CmlLogo from "public/shared/cmlLogo.png";

export type HeroProps = {
  title: string;
  desc?: string;
  button: {
    link: string;
    label: string;
  };
  image?: string;
  button2?: {
    link: string;
    label: string;
  };
  cmlPowered?: boolean;
};

const HomeHeroCard = ({
  title,
  desc,
  button,
  image,
  button2,
  cmlPowered = false,
}: HeroProps) => {
  const scrollToElementId1 = button.link;
  const scrollToElementId2 = button2?.link;

  return (
    <Box
      position="relative"
      minH={{ base: "440px", md: "500px" }}
      color="white"
      overflow="hidden"
    >
      <Box position="absolute" inset="0" bg="brand.200" zIndex={-2} />
      {image && (
        <Image
          src={image}
          alt="Hero background"
          fill
          priority
          quality={75}
          style={{ objectFit: "cover", zIndex: -1 }}
        />
      )}
      <PageLayout>
        <Stack
          gap={8}
          width={{ base: "100%", md: "70%", lg: "60%", xl: "50%" }}
          py={{ base: 8, sm: 16 }}
        >
          <Heading
            as="h1"
            fontSize={{ base: "38px", md: "48px" }}
            lineHeight={{ base: "40px", md: "54px", lg: "58px" }}
            fontWeight={700}
            whiteSpace="pre-line"
          >
            {title}
          </Heading>

          {desc && (
            <Text
              fontSize="24px"
              lineHeight="30px"
              fontWeight={300}
              whiteSpace="pre-line"
            >
              {desc}
            </Text>
          )}

          <Stack gap={{ base: 2, md: 6 }} direction={{ base: "column", md: "row" }}>
            <Button
              onClick={() => scrollToElement(scrollToElementId1, 120)}
              variant={"outline"}
              fontSize={{ base: "16px", md: "20px" }}
              whiteSpace="normal"
              w="fit-content"
              py={{ base: 5 }}
            >
              {button.label}
              <FiArrowUpRight />
            </Button>

            {button2 && scrollToElementId2 && (
              <Button
                onClick={() => scrollToElement(scrollToElementId2, 120)}
                variant={"outline"}
                fontSize={{ base: "16px", md: "20px" }}
                w="fit-content"
              >
                {button2.label}
              </Button>
            )}
          </Stack>
        </Stack>
      </PageLayout>

      {cmlPowered && (
        <Link href="https://cloudmylab.com" target="_blank" rel="noopener noreferrer">
          <Box
            position="fixed"
            bottom="10px"
            right="10px"
            bg="white"
            _dark={{ bg: "gray.800" }}
            px={3}
            py={2}
            borderRadius="md"
            boxShadow="md"
            zIndex={9999}
            cursor="pointer"
            _hover={{ transform: "scale(1.02)" }}
            transition="transform 0.2s"
          >
            <Text
              fontSize="16px"
              color="gray.600"
              _dark={{ color: "gray.300" }}
              alignSelf={"center"}
            >
              Powered by
              <Image
                src={CmlLogo}
                alt="CML"
                width={100}
                height={100}
                style={{ display: "inline-block", verticalAlign: "middle" }}
                loading="lazy"
              />
            </Text>
          </Box>
        </Link>
      )}
    </Box>
  );
};

export default HomeHeroCard;
